import { useState, useRef, useEffect } from 'react';
import api, { API_URL } from '../../../api';
import toast from 'react-hot-toast';
import '../../../assets/css/pages/measures/copyMeasuresModal.css';

const CopyMeasuresModal = ({ open, onClose, currentYear, onActionComplete }) => {
    const [selectedMeasures, setSelectedMeasures] = useState([]);
    const [targetYear, setTargetYear] = useState('');
    const [sourceYear, setSourceYear] = useState('');
    const [availableYears, setAvailableYears] = useState([]);
    const [sourceMeasures, setSourceMeasures] = useState([]);
    const [isLoadingYears, setIsLoadingYears] = useState(false);
    const [isLoadingMeasures, setIsLoadingMeasures] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [yearsError, setYearsError] = useState(null);
    const modalRef = useRef(null);

    const getTargetYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const backendYears = availableYears.map(y => parseInt(y)).filter(y => !isNaN(y));
        const maxBackendYear = backendYears.length > 0 ? Math.max(...backendYears) : currentYear;
        const futureYears = [];
        for (let y = maxBackendYear + 1; y <= currentYear + 3; y++) {
            futureYears.push(y);
        }
    
        const allYears = Array.from(new Set([...backendYears, ...futureYears]))
            .filter(y => y > 2000 && y < 2100)
            .sort((a, b) => b - a);
        return allYears;
    };


    useEffect(() => {
        const fetchAvailableYears = async () => {
            setIsLoadingYears(true);
            setYearsError(null);
            console.log('Fetching years from:', `${API_URL}/measures/years/`);
            try {
                const response = await api.get(`${API_URL}/measures/years/`);
                console.log('Years API Response:', { status: response.status, data: response.data });

                if (response.status === 200) {
                    const { years, count } = response.data;
                    console.log('Extracted years:', years, 'Count:', count);

                    if (!Array.isArray(years)) {
                        console.warn('Years is not an array:', years);
                        throw new Error('Invalid response format: years is not an array');
                    }

                    const uniqueYears = [...new Set(
                        years.map(year => parseInt(year))
                             .filter(year => !isNaN(year) && year > 0)
                    )].sort((a, b) => b - a);
                    console.log('Processed unique years:', uniqueYears);

                    if (count !== uniqueYears.length) {
                        console.warn(`Count mismatch: API count=${count}, processed years=${uniqueYears.length}`);
                    }

                    setAvailableYears(uniqueYears);

                    if (uniqueYears.length > 0) {
                        const defaultYear = uniqueYears.includes(parseInt(currentYear)) 
                            ? currentYear.toString() 
                            : uniqueYears[0].toString();
                        setSourceYear(defaultYear);
                        console.log('Set default source year to:', defaultYear);
                    } else {
                        setYearsError('No years available in the system.');
                        toast.warn('No years found. Please ensure measures exist.');
                    }
                } else {
                    throw new Error(`Unexpected status code: ${response.status}`);
                }
            } catch (error) {
                const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message;
                console.error('Error fetching years:', { message: errorMsg, response: error.response });
                setYearsError('Failed to load years. Please try again or contact support.');
                toast.error('Unable to fetch years. Check your connection or contact support.');
                setAvailableYears([]);
            } finally {
                setIsLoadingYears(false);
            }
        };

        if (open) {
            console.log('Modal opened at', new Date().toISOString(), 'triggering years fetch');
            fetchAvailableYears();
        }
    }, [open, currentYear]);

    useEffect(() => {
        const fetchMeasuresForYear = async () => {
            if (!sourceYear) {
                console.log('No source year selected, skipping measures fetch');
                return;
            }
            
            setIsLoadingMeasures(true);
            try {
                console.log('Fetching measures for year (new endpoint):', sourceYear);
                const response = await api.get(`${API_URL}/measures/by-year/`, {
                    params: { year: sourceYear }
                });
                console.log(`Measures API Response for ${sourceYear}:`, {
                    status: response.status,
                    data: response.data
                });
                if (response.status === 200) {
                    let measuresData = response.data?.measures || [];
                    if (!Array.isArray(measuresData)) {
                        measuresData = [];
                    }
                    console.log('Processed measures:', measuresData);
                    setSourceMeasures(measuresData);
                    setSelectedMeasures([]);
                    if (measuresData.length === 0) {
                        toast.info(`No measures found for ${sourceYear}. Please select another year or check filters.`);
                    }
                }
            } catch (error) {
                const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message;
                console.error(`Error fetching measures for ${sourceYear}:`, errorMsg);
                toast.error(`Failed to fetch measures for ${sourceYear}.`);
                setSourceMeasures([]);
            } finally {
                setIsLoadingMeasures(false);
            }
        };

        fetchMeasuresForYear();
    }, [sourceYear]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, onClose]);

    const handleMeasureSelect = (measureName) => {
        setSelectedMeasures(prev => {
            if (prev.includes(measureName)) {
                return prev.filter(name => name !== measureName);
            }
            return [...prev, measureName];
        });
    };

    const handleSelectAll = () => {
        if (selectedMeasures.length === sourceMeasures.length) {
            setSelectedMeasures([]);
        } else {
            setSelectedMeasures(sourceMeasures.map(measure => measure.name));
        }
    };

    const handleCopyMeasures = async () => {
        if (selectedMeasures.length === 0) {
            toast.error('Please select at least one measure to copy');
            return;
        }
        if (!targetYear) {
            toast.error('Please select a target year');
            return;
        }
        if (sourceYear === targetYear) {
            toast.error('Source and target years cannot be the same');
            return;
        }
        if (!/^[0-9]{4}$/.test(targetYear)) {
            toast.error('Target year must be a 4-digit number');
            return;
        }
        setIsCopying(true);
        try {
            const payload = {
                source_year: parseInt(sourceYear),
                target_year: parseInt(targetYear),
                measure_names: selectedMeasures
            };
            console.log('Sending copy measures payload:', payload);
            const response = await api.post(`${API_URL}/measures/copy-measures/`, payload);
    
            
            const {copied_count, skipped_count, skipped_measures, copied_measures } = response.data;
            
            const combinedMessage = [];
            
            if (copied_count > 0) {
                combinedMessage.push(`✓ Successfully copied ${copied_count} measure${copied_count > 1 ? 's' : ''}`);
                if (copied_measures?.length > 0) {
                    combinedMessage.push(`Copied: ${copied_measures.join(', ')}`);
                }
            }
            
            if (skipped_count > 0) {
                combinedMessage.push(`⚠️ ${skipped_count} measure${skipped_count > 1 ? 's were' : ' was'} not copied:`);
                skipped_measures.forEach(measure => {
                    combinedMessage.push(`• ${measure} already exists in ${targetYear}`);
                });
            }
            
          
            const toastOptions = {
                duration: 5000,
                style: {
                    maxWidth: '500px',
                    whiteSpace: 'pre-wrap'
                }
            };
            
            if (copied_count > 0) {
                toast.success(combinedMessage.join('\n'), toastOptions);
            } else {
                toast.error(combinedMessage.join('\n'), toastOptions);
            }
            
            if (typeof onActionComplete === 'function') {
                onActionComplete();
            }
            
            onClose();
            
            setTimeout(() => {
                window.location.reload();
            }, 3000);
        } catch (error) {
            const errorMsg = error.response?.data?.error || 
                            error.response?.data?.detail || 
                            error.message || 
                            'An unexpected error occurred while copying measures';
            console.error('Error copying measures:', errorMsg, error.response?.data);
            toast.error(`Error copying measures: ${errorMsg}`);
        } finally {
            setIsCopying(false);
        }
    };

    if (!open) return null;


    return (
        <div className="overlay">
            <div className="popup-content popup-user copy-measures-modal" ref={modalRef}>
                <h2>Copy Measures to Another Year</h2>
                <div className="modal-content">
                    <div className="form-control">
                        <label>Source Year</label>
                        {isLoadingYears ? (
                            <div>Loading years...</div>
                        ) : yearsError ? (
                            <div className="error-message">{yearsError}</div>
                        ) : availableYears.length === 0 ? (
                            <div className="no-data">No years available</div>
                        ) : (
                            <select
                                value={sourceYear}
                                onChange={(e) => setSourceYear(e.target.value)}
                                disabled={isCopying || isLoadingMeasures}
                            >
                                <option value="" disabled>Select Source Year</option>
                                {availableYears.map(year => (
                                    <option key={`source-${year}`} value={year}>{year}</option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className="form-control">
                        <label>Target Year</label>
                        <select
                            value={targetYear}
                            onChange={e => setTargetYear(e.target.value)}
                            disabled={isCopying || isLoadingMeasures}
                        >
                            <option value="" disabled>Select Target Year</option>
                            {getTargetYearOptions().map(year => (
                                <option key={`target-${year}`} value={year}>{year}</option>
                            ))}
                        </select>
                        <small style={{ color: '#888' }}>You can select up to 3 years ahead of the current year</small>
                    </div>

                    <div className="form-control">
                        <label>Select Measures</label>
                        {isLoadingMeasures ? (
                            <div>Loading measures...</div>
                        ) : sourceMeasures.length === 0 ? (
                            <div className="no-measures">
                                {sourceYear ? `No measures available for ${sourceYear}.` : 'Please select a source year.'}
                            </div>
                        ) : (
                            <div className="measures-list">
                                <div className="select-all">
                                    <input
                                        type="checkbox"
                                        checked={selectedMeasures.length === sourceMeasures.length && sourceMeasures.length > 0}
                                        onChange={handleSelectAll}
                                        disabled={isCopying || isLoadingMeasures}
                                    />
                                    <span>Select All</span>
                                </div>
                                {sourceMeasures.map((measure) => (
                                    <div key={measure.measure_id || measure.name} className="measure-item">
                                        <input
                                            type="checkbox"
                                            checked={selectedMeasures.includes(measure.name)}
                                            onChange={() => handleMeasureSelect(measure.name)}
                                            disabled={isCopying || isLoadingMeasures}
                                        />
                                        <span>{measure.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="action-buttons">
                    <button className="cancel-button" onClick={onClose} disabled={isCopying}>
                        Cancel
                    </button>
                    <button 
                        className="copy-button"
                        onClick={handleCopyMeasures} 
                        disabled={isCopying || isLoadingMeasures || selectedMeasures.length === 0 || !targetYear || !sourceYear}
                    >
                        {isCopying ? 'Copying...' : 'Copy Measures'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CopyMeasuresModal;